import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearInvalidCloudSession,
  isCloudSessionValidForHost,
} from "../utils/cloudBrowserSession";

const CloudLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [rootDomain, setRootDomain] = useState("");

  const safeGet = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (_err) {
      return null;
    }
  };

  const safeSet = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (_err) {
      // ignore storage errors (private mode / blocked)
    }
  };

  React.useEffect(() => {
    try {
      const existing = safeGet("moo_cloud_user");
      const existingToken = safeGet("moo_cloud_token");
      if (existing && existingToken) {
        const existingUser = JSON.parse(existing);
        const sessionMatchesHost = isCloudSessionValidForHost({
          hostname: window.location?.hostname || "",
          user: existingUser,
          token: existingToken,
        });
        if (!sessionMatchesHost) {
          clearInvalidCloudSession(window.localStorage);
          return;
        }
        const existingRole = String(existingUser?.role || "").toLowerCase();
        // Sur un sous-domaine établissement, on bascule vers l'admin local (UI).
        // Sur le domaine hub, on reste sur le dashboard cloud Djizef.
        const isTenant = Boolean(subdomain);
        const target = isTenant
          ? existingRole === "stockeur"
            ? "/waise"
            : "/admin"
          : "/cloud/dashboard";
        navigate(target, { replace: true });
      }
    } catch (e) {
      console.warn("Navigation auto cloud bloquée:", e?.message || e);
    }
  }, [navigate, subdomain]);

  React.useEffect(() => {
    try {
      const host = window.location.hostname || "";
      const parts = host.split(".");
      if (parts.length >= 3) {
        setSubdomain(parts[0] || "");
        setRootDomain(parts.slice(1).join("."));
      }
    } catch (_e) {
      // ignore
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    const username = credentials.username.trim();
    const password = credentials.password;
    fetch("/api/cloud/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password, subdomain }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || "Identifiants invalides");
        }
        return res.json();
      })
      .then((data) => {
        safeSet("moo_cloud_user", JSON.stringify(data.user));
        safeSet("moo_cloud_token", data.token);
        // Pour utiliser l'interface admin classique en cloud, on réutilise les clés locales
        safeSet("moo_user", JSON.stringify(data.user));
        safeSet("moo_token", data.token);
        try {
          const role = String(data.user?.role || "").toLowerCase();
          const requestedReturn = new URLSearchParams(location.search).get("returnTo");
          const safeReturn = requestedReturn && requestedReturn.startsWith("/") && !requestedReturn.startsWith("//")
            ? requestedReturn
            : null;
          const target = safeReturn || (
            role === "cloud-superadmin"
              ? "/cloud/dashboard"
              : role === "stockeur"
                ? "/waise"
                : "/admin"
          );
          navigate(target, { replace: true });
          // La navigation doit précéder le remontage lié à l'authentification.
          // Sinon /cloud/login peut rendre momentanément le dashboard siège et
          // révoquer à tort une session établissement parfaitement valide.
          window.setTimeout(() => {
            try {
              window.dispatchEvent(new Event("cloud-auth-changed"));
            } catch (_e) {
              // ignore
            }
          }, 0);
        } catch (navErr) {
          console.warn("Navigation cloud bloquée:", navErr?.message || navErr);
        }
      })
      .catch((e) => setError(e.message));
  };

  const hasSubdomain = !!subdomain;
  const heading = hasSubdomain ? "Connexion établissement" : "Connexion Cloud";
  const subtitle = hasSubdomain
    ? `Sous-domaine : ${subdomain}.${rootDomain || "moosync.com"}`
    : "Accès siège Djizef SARL";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1224] via-[#0b152d] to-[#050b18] text-white flex items-center justify-center px-4">
      <div className="max-w-md w-full rounded-3xl bg-white/5 border border-white/10 p-8 shadow-2xl shadow-amber-500/20">
        <div className="mb-6 text-center space-y-1">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300">{heading}</p>
          <div className="flex items-center justify-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center shadow-lg shadow-black/20">
              <img
                src="/moo-sync-insigne-logo.png"
                alt="MOO Sync"
                className="h-6 w-6 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold">MOO Sync</h1>
          </div>
          <p className="text-white/60 text-sm">{subtitle}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {hasSubdomain && (
            <div className="text-center text-xs text-white/50 bg-white/5 rounded-2xl border border-white/10 px-3 py-2">
              Vous vous connectez à l’établissement <span className="font-semibold text-white">{subdomain}</span>
            </div>
          )}
          <div className="space-y-2">
            <label className="text-sm text-white/80">Identifiant</label>
            <input
              type="text"
              value={credentials.username}
              onChange={(e) => setCredentials((p) => ({ ...p, username: e.target.value }))}
              className="w-full rounded-2xl bg-white/10 border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
              placeholder="Email ou identifiant admin/manager"
              autoComplete="username"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm text-white/80">Mot de passe</label>
            <input
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials((p) => ({ ...p, password: e.target.value }))}
              className="w-full rounded-2xl bg-white/10 border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
              placeholder="Votre mot de passe"
              autoComplete="current-password"
            />
          </div>
          {error && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/40 rounded-2xl px-4 py-2">{error}</div>}
          <button
            type="submit"
            className="w-full rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 font-semibold py-3 shadow-lg hover:shadow-amber-400/30 transition"
          >
            Se connecter
          </button>
        </form>
      </div>
    </div>
  );
};

export default CloudLogin;
